import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DEFAULT_STAGES = [
  { key: "draft", label: "Draft", approver_role: "ceo_leadership" },
  { key: "submitted", label: "Submitted", approver_role: "aikyam_leadership" },
  { key: "validated", label: "Validated", approver_role: "aikyam_leadership" },
  { key: "approved", label: "Approved", approver_role: "chairman" },
];

export default function AdminWorkflows() {
  const { tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", framework: "panchatanthra" });

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/workflows`).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-workflows-header"
        eyebrow="Administration"
        title="Workflow manager"
        description="Stage rules, approvers and escalation are versioned. Publishing a version retires the previous one."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-workflow-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">New workflow</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-display text-2xl">Workflow definition</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-caps">Name</Label>
                  <Input data-testid="workflow-name-input" className="rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Framework</Label>
                  <Select value={form.framework} onValueChange={(v) => setForm({ ...form, framework: v })}>
                    <SelectTrigger data-testid="workflow-framework-select" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="panchatanthra">panchatanthra</SelectItem>
                      <SelectItem value="viveka_kartha">viveka_kartha</SelectItem>
                      <SelectItem value="dinacharya">dinacharya</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">Seeded with the standard four-stage governance chain.</p>
                <Button
                  data-testid="create-workflow-submit"
                  className="w-full rounded-none"
                  disabled={!form.name}
                  onClick={async () => {
                    try {
                      await api.post(`/tenants/${tenantId}/workflows`, { ...form, stages: DEFAULT_STAGES });
                      toast.success("Workflow created as draft v1");
                      setOpen(false);
                      setForm({ name: "", framework: "panchatanthra" });
                      load();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  Create workflow
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="space-y-6" data-testid="workflows-list">
        {rows.map((w) => (
          <div key={w.id} className="panel">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <p className="font-display text-xl">{w.name}</p>
                <p className="label-caps mt-1">{w.framework}</p>
              </div>
            </div>
            <div className="divide-y divide-border">
              {(w.versions || []).map((v) => (
                <div key={v.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div>
                    <p className="text-sm">{v.label}</p>
                    <p className="label-caps mt-1">{(v.stages || []).map((s) => `${s.label} (${s.approver_role})`).join(" → ")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StateBadge state={v.state} />
                    {v.state === "draft" && (
                      <Button
                        data-testid={`publish-workflow-${v.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                        onClick={async () => {
                          try {
                            await api.post(`/tenants/${tenantId}/workflow-versions/${v.id}/publish`);
                            toast.success("Workflow version published");
                            load();
                          } catch (e) {
                            toast.error(errMsg(e));
                          }
                        }}
                      >
                        Publish
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <EmptyState testid="workflows-empty" title="No workflows" message="Define stage rules and approvers per framework." />}
      </div>
    </div>
  );
}
